import "./upload-file.scss";
import { useEffect, useState } from "react";
import { downLoadFileById, fileUpload } from "../../services/fileServices";
import { toast } from "react-toastify";
import { actualFileType, DEFAULT_FILE_TYPES, FILE_TYPE } from "./uploadFileConsts";
import clsx from "clsx";
import { IFile } from "../../profile/models/dialogModels";
import CustomTooltip from "../custom-tooltip";
import useMultiLanguage from "../../../hook/useMultiLanguage";

type IProps = {
  label: string;
  required?: boolean;
  setValue: (data: IFile) => void;
  fileValue: IFile;
  fileType: string;
  allowFileTypes?: string[];
  errors?: string;
  isReadOnly?: boolean;
};

function UploadFile(props: IProps) {
  const { label, required, setValue, fileValue, errors, allowFileTypes = DEFAULT_FILE_TYPES, isReadOnly, fileType } = props;
  const { lang } = useMultiLanguage();
  const [selectedFile, setSelectedFile] = useState<IFile>({} as IFile);

  useEffect(() => {
    setSelectedFile(fileValue);
  }, [fileValue])

  const onChange = async (e: any) => {
    let file = e.target.files[0];
    if(file) {
      const isValidMaxSize = file.size > 20 * 1024 * 1024;
      const isValidFileType = actualFileType.find((item) => allowFileTypes.includes(item.id) && item.value === file.type);
      if (!isValidFileType) {
        toast.error(lang("TOAST.ERROR.FORMAT.FILE"));
        return;
      }
      if (isValidMaxSize) {
        toast.error(lang("TOAST.ERROR.LARGE.FILE"))
        return;
      }
      try {
        const { data: { data }} = await fileUpload(file, fileType);
        const fileObject : IFile = {
          id: data?.id,
          name: data?.name
        };
        setSelectedFile(fileObject);
        setValue(fileObject);
      } catch (error) {
        toast.error(lang("GENERAL.ERROR"));
      }
    }
  };

  const handleDownLoadFile = async (file: IFile) => {
    try {
      const res = await downLoadFileById(file.id);
      const url = window.URL.createObjectURL(new Blob([res?.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${file.name}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error(lang("GENERAL.ERROR"));
    }
  };
  
  const handleShowPreviewFile = async (file: IFile) => {
    try {
      const res = await downLoadFileById(file.id);
      const contentType = res?.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([res.data], { type: contentType });
      
      const url = window.URL.createObjectURL(blob);
      if (contentType.includes('image') || contentType === actualFileType.find(item => item.id === FILE_TYPE.PDF)?.value) {
        window.open(url, '_blank');
      } else {
        toast.warning(lang("TOAST.ERROR.UNSUPPORTED.PREVIEW"))
      }
    } catch (error) {
      toast.error(lang("GENERAL.ERROR"));
    }
  };

  return (
    <>
      <div className="text-lable-input lable">
        <span>{lang(label)}</span>
        {required && <span className="color-red"> *</span>}
      </div>
      <div className={clsx(
        "custom-file-upload gap-2",
        { "form-control is-invalid": errors },
        { "disabled": isReadOnly }
      )}>
        <CustomTooltip
          title={lang("SELECT.FILE")}
          placement="auto"
          className={clsx(
            { "hidden": isReadOnly }
          )}
        >
          <label
            className={clsx("flex flex-middle flex-shrink-1 flex-grow-0 overflow-hidden",
              { "cursor-pointer": !isReadOnly }
            )}
          >
            <input type="file" onChange={onChange} accept={allowFileTypes.join(',')} readOnly={isReadOnly} disabled={isReadOnly} />
            <div className="icon-upload-file">
              <i className="bi bi-cloud-arrow-up-fill text-primary me-1 fs-4" />
            </div>
            <span className="file-preview">{selectedFile?.name || ""}</span>
          </label>
        </CustomTooltip>
        <div className="flex gap-5 spaces fs-16">
          {selectedFile.id && (
            <CustomTooltip
              title={lang("VIEW.FILE")}
              placement="right"
            >
              <div
                className="bi bi-eye cursor-pointer"
                onClick={() => handleShowPreviewFile(selectedFile)}
              ></div>
            </CustomTooltip>
          )}
          {selectedFile.id && (
            <CustomTooltip
              title={lang("DOWNLOAD.FILE")}
              placement="right"
            >
              <div
                className="bi bi-download cursor-pointer"
                onClick={() => handleDownLoadFile(selectedFile)}
              ></div>
            </CustomTooltip>
          )}
        </div>
      </div>
      {errors && <div className='invalid-feedback'>{errors}</div>}
    </>
  );
}

export default UploadFile;
